import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState, useCallback } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useSuperAdmin } from "@/hooks/useSuperAdmin";
import {
  listCommercialEvents,
  listCommercialCampaigns,
  listCommercialRules,
  getCommercialAnalytics,
  recordCommercialEvent,
  upsertCommercialCampaign,
  upsertCommercialRule,
  toggleCommercialRule,
  type CommercialEventRow,
  type CommercialCampaignRow,
  type CommercialRuleRow,
  type CommercialAnalyticsRow,
} from "@/lib/commercial/commercial.functions";

type EventInput = {
  brand_name: string;
  category?: string;
  match_id?: string;
  country?: string;
  channel?: string;
  duration_seconds?: number;
};
type CampaignInput = {
  id?: string;
  name: string;
  sponsor_brand: string;
  sponsor_category?: string;
  headline: string;
  cta_label?: string;
  country?: string;
  is_active?: boolean;
};
type RuleInput = {
  id?: string;
  trigger_brand?: string;
  trigger_category?: string;
  country?: string;
  priority?: number;
  campaign_id: string;
  is_active?: boolean;
};

export const Route = createFileRoute("/admin/commercial-intelligence")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Commercial Intelligence · VerseSync" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: CommercialIntelligencePage,
});

type Tab = "dashboard" | "events" | "campaigns" | "rules" | "analytics" | "timeline";

function CommercialIntelligencePage() {
  const { isSuperAdmin, isLoading } = useSuperAdmin();
  const [tab, setTab] = useState<Tab>("dashboard");
  const [events, setEvents] = useState<CommercialEventRow[]>([]);
  const [campaigns, setCampaigns] = useState<CommercialCampaignRow[]>([]);
  const [rules, setRules] = useState<CommercialRuleRow[]>([]);
  const [analytics, setAnalytics] = useState<CommercialAnalyticsRow[]>([]);
  const [err, setErr] = useState<string | null>(null);

  const loadEvents = useServerFn(listCommercialEvents);
  const loadCampaigns = useServerFn(listCommercialCampaigns);
  const loadRules = useServerFn(listCommercialRules);
  const loadAnalytics = useServerFn(getCommercialAnalytics);
  const seedEvent = useServerFn(recordCommercialEvent);
  const saveCampaign = useServerFn(upsertCommercialCampaign);
  const saveRule = useServerFn(upsertCommercialRule);
  const toggleRule = useServerFn(toggleCommercialRule);

  const refresh = useCallback(async () => {
    try {
      const [ev, cp, rl, an] = await Promise.all([
        loadEvents(), loadCampaigns(), loadRules(), loadAnalytics(),
      ]);
      setEvents(ev); setCampaigns(cp); setRules(rl); setAnalytics(an);
      setErr(null);
    } catch (e) {
      setErr((e as Error).message);
    }
  }, [loadEvents, loadCampaigns, loadRules, loadAnalytics]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    void refresh();
  }, [isSuperAdmin, refresh]);

  if (isLoading) return <main className="p-4 text-sm">Verificando acceso…</main>;
  if (!isSuperAdmin) {
    return (
      <main className="mx-auto max-w-md p-4 text-sm">
        <p className="font-bold">Acceso restringido</p>
        <Link to="/" className="text-primary underline">Volver</Link>
      </main>
    );
  }

  const totals = analytics.reduce(
    (acc, r) => {
      acc.impressions += r.impressions;
      acc.clicks += r.clicks;
      acc.conversions += r.conversions;
      acc.shares += r.shares;
      return acc;
    },
    { impressions: 0, clicks: 0, conversions: 0, shares: 0 },
  );
  const globalCtr = totals.impressions > 0
    ? Math.round((totals.clicks / totals.impressions) * 10000) / 100
    : 0;

  return (
    <main className="mx-auto max-w-5xl space-y-4 px-3 py-4">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-xl font-black">🎯 Commercial Intelligence Center</h1>
          <p className="text-[12px] text-muted-foreground">
            Detección comercial · Campañas patrocinadas · Reglas · Telemetría
          </p>
        </div>
        <button
          onClick={refresh}
          className="rounded-lg border border-border bg-background px-3 py-1 text-[11px] font-bold hover:bg-accent"
        >
          Actualizar
        </button>
      </header>

      <nav className="flex flex-wrap gap-1 rounded-xl border border-border bg-background/60 p-1 text-[11px]">
        {(["dashboard","events","campaigns","rules","analytics","timeline"] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`flex-1 rounded-lg px-2 py-1.5 font-bold capitalize ${
              tab === t ? "bg-primary text-primary-foreground" : "hover:bg-accent"
            }`}
          >
            {t}
          </button>
        ))}
      </nav>

      {err && (
        <p className="rounded bg-destructive/10 p-2 text-[11px] text-destructive">{err}</p>
      )}

      {tab === "dashboard" && (
        <section className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Kpi label="Impresiones" value={totals.impressions} />
          <Kpi label="Clics" value={totals.clicks} />
          <Kpi label="CTR" value={`${globalCtr}%`} />
          <Kpi label="Conversiones" value={totals.conversions} />
          <Kpi label="Eventos detectados" value={events.length} />
          <Kpi label="Campañas activas" value={campaigns.filter((c) => c.is_active).length} />
          <Kpi label="Reglas activas" value={rules.filter((r) => r.is_active).length} />
          <Kpi label="Shares" value={totals.shares} />
        </section>
      )}

      {tab === "events" && (
        <EventsPanel events={events} onSeed={seedEvent} onRefresh={refresh} />
      )}

      {tab === "campaigns" && (
        <CampaignsPanel campaigns={campaigns} onSave={saveCampaign} onRefresh={refresh} />
      )}

      {tab === "rules" && (
        <RulesPanel
          rules={rules}
          campaigns={campaigns}
          onSave={saveRule}
          onToggle={toggleRule}
          onRefresh={refresh}
        />
      )}

      {tab === "analytics" && <AnalyticsPanel rows={analytics} />}

      {tab === "timeline" && <TimelinePanel events={events} />}
    </main>
  );
}

function Kpi({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-xl border border-border bg-background/60 p-3">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 text-xl font-black">{value}</div>
    </div>
  );
}

/* ============= Events ============= */
function EventsPanel({
  events, onSeed, onRefresh,
}: {
  events: CommercialEventRow[];
  onSeed: (args: { data: EventInput }) => Promise<unknown>;
  onRefresh: () => Promise<void>;
}) {
  const [form, setForm] = useState({
    brand_name: "", category: "", match_id: "", country: "", channel: "",
    duration_seconds: 30,
  });
  const [saving, setSaving] = useState(false);
  return (
    <section className="space-y-3">
      <div className="rounded-xl border border-border bg-background/60 p-3">
        <h2 className="text-sm font-black">Registrar evento comercial</h2>
        <div className="mt-2 grid grid-cols-2 gap-2 text-[11px] sm:grid-cols-3">
          <Input label="Marca *" value={form.brand_name} onChange={(v) => setForm({ ...form, brand_name: v })} />
          <Input label="Categoría" value={form.category} onChange={(v) => setForm({ ...form, category: v })} />
          <Input label="Match ID" value={form.match_id} onChange={(v) => setForm({ ...form, match_id: v })} />
          <Input label="País" value={form.country} onChange={(v) => setForm({ ...form, country: v })} />
          <Input label="Canal" value={form.channel} onChange={(v) => setForm({ ...form, channel: v })} />
          <Input
            label="Duración (s)" type="number"
            value={String(form.duration_seconds)}
            onChange={(v) => setForm({ ...form, duration_seconds: Math.max(5, Number(v) || 30) })}
          />
        </div>
        <button
          disabled={saving || !form.brand_name.trim()}
          onClick={async () => {
            setSaving(true);
            try {
              await onSeed({
                data: {
                  brand_name: form.brand_name.trim(),
                  category: form.category.trim() || undefined,
                  match_id: form.match_id.trim() || undefined,
                  country: form.country.trim() || undefined,
                  channel: form.channel.trim() || undefined,
                  duration_seconds: form.duration_seconds,
                },
              });
              setForm({ ...form, brand_name: "" });
              await onRefresh();
            } finally {
              setSaving(false);
            }
          }}
          className="mt-2 rounded-lg bg-primary px-3 py-1.5 text-[11px] font-black text-primary-foreground disabled:opacity-50"
        >
          {saving ? "Guardando…" : "Registrar"}
        </button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-border bg-background/60 p-3">
        <h2 className="text-sm font-black">Eventos recientes</h2>
        <table className="mt-2 w-full text-left text-[11px]">
          <thead className="text-muted-foreground">
            <tr><th className="px-2 py-1">Marca</th><th className="px-2 py-1">Categoría</th><th className="px-2 py-1">Canal</th><th className="px-2 py-1">País</th><th className="px-2 py-1">Match</th><th className="px-2 py-1">Detectado</th><th className="px-2 py-1">Dur.</th></tr>
          </thead>
          <tbody>
            {events.map((e) => (
              <tr key={e.id} className="border-t border-border/40">
                <td className="px-2 py-1 font-black">{e.brand_name}</td>
                <td className="px-2 py-1">{e.category ?? "—"}</td>
                <td className="px-2 py-1">{e.channel ?? "—"}</td>
                <td className="px-2 py-1">{e.country ?? "—"}</td>
                <td className="px-2 py-1">{e.match_id ?? "—"}</td>
                <td className="px-2 py-1">{new Date(e.detected_at).toLocaleString()}</td>
                <td className="px-2 py-1">{e.duration_seconds}s</td>
              </tr>
            ))}
            {events.length === 0 && (
              <tr><td colSpan={7} className="px-2 py-3 text-center text-muted-foreground">Sin eventos.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/* ============= Campaigns ============= */
function CampaignsPanel({
  campaigns, onSave, onRefresh,
}: {
  campaigns: CommercialCampaignRow[];
  onSave: (args: { data: CampaignInput }) => Promise<unknown>;
  onRefresh: () => Promise<void>;
}) {
  const [form, setForm] = useState({
    name: "", sponsor_brand: "", sponsor_category: "", headline: "",
    cta_label: "Participar", country: "",
  });
  const [saving, setSaving] = useState(false);
  return (
    <section className="space-y-3">
      <div className="rounded-xl border border-border bg-background/60 p-3">
        <h2 className="text-sm font-black">Nueva campaña</h2>
        <div className="mt-2 grid grid-cols-2 gap-2 text-[11px] sm:grid-cols-3">
          <Input label="Nombre *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
          <Input label="Marca patrocinadora *" value={form.sponsor_brand} onChange={(v) => setForm({ ...form, sponsor_brand: v })} />
          <Input label="Categoría" value={form.sponsor_category} onChange={(v) => setForm({ ...form, sponsor_category: v })} />
          <Input label="Headline *" value={form.headline} onChange={(v) => setForm({ ...form, headline: v })} />
          <Input label="CTA" value={form.cta_label} onChange={(v) => setForm({ ...form, cta_label: v })} />
          <Input label="País" value={form.country} onChange={(v) => setForm({ ...form, country: v })} />
        </div>
        <button
          disabled={saving || !form.name.trim() || !form.sponsor_brand.trim() || !form.headline.trim()}
          onClick={async () => {
            setSaving(true);
            try {
              await onSave({
                data: {
                  name: form.name.trim(),
                  sponsor_brand: form.sponsor_brand.trim(),
                  sponsor_category: form.sponsor_category.trim() || undefined,
                  headline: form.headline.trim(),
                  cta_label: form.cta_label.trim() || "Participar",
                  country: form.country.trim() || undefined,
                  is_active: true,
                },
              });
              setForm({ name: "", sponsor_brand: "", sponsor_category: "", headline: "", cta_label: "Participar", country: "" });
              await onRefresh();
            } finally { setSaving(false); }
          }}
          className="mt-2 rounded-lg bg-primary px-3 py-1.5 text-[11px] font-black text-primary-foreground disabled:opacity-50"
        >
          {saving ? "Guardando…" : "Crear campaña"}
        </button>
      </div>
      <div className="overflow-x-auto rounded-xl border border-border bg-background/60 p-3">
        <h2 className="text-sm font-black">Campañas</h2>
        <table className="mt-2 w-full text-left text-[11px]">
          <thead className="text-muted-foreground">
            <tr><th className="px-2 py-1">Nombre</th><th className="px-2 py-1">Marca</th><th className="px-2 py-1">Headline</th><th className="px-2 py-1">CTA</th><th className="px-2 py-1">País</th><th className="px-2 py-1">Activa</th></tr>
          </thead>
          <tbody>
            {campaigns.map((c) => (
              <tr key={c.id} className="border-t border-border/40">
                <td className="px-2 py-1 font-black">{c.name}</td>
                <td className="px-2 py-1">{c.sponsor_brand}</td>
                <td className="px-2 py-1">{c.headline}</td>
                <td className="px-2 py-1">{c.cta_label}</td>
                <td className="px-2 py-1">{c.country ?? "—"}</td>
                <td className="px-2 py-1">{c.is_active ? "✓" : "—"}</td>
              </tr>
            ))}
            {campaigns.length === 0 && (
              <tr><td colSpan={6} className="px-2 py-3 text-center text-muted-foreground">Sin campañas.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/* ============= Rules ============= */
function RulesPanel({
  rules, campaigns, onSave, onToggle, onRefresh,
}: {
  rules: CommercialRuleRow[];
  campaigns: CommercialCampaignRow[];
  onSave: (args: { data: RuleInput }) => Promise<unknown>;
  onToggle: (args: { data: { id: string; is_active: boolean } }) => Promise<unknown>;
  onRefresh: () => Promise<void>;
}) {
  const [form, setForm] = useState({
    trigger_brand: "", trigger_category: "", country: "",
    priority: 100, campaign_id: "",
  });
  const [saving, setSaving] = useState(false);
  return (
    <section className="space-y-3">
      <div className="rounded-xl border border-border bg-background/60 p-3">
        <h2 className="text-sm font-black">Nueva regla</h2>
        <p className="text-[10px] text-muted-foreground">
          Mapea una marca detectada (ej. Coca-Cola) a una campaña patrocinada (ej. Pepsi).
        </p>
        <div className="mt-2 grid grid-cols-2 gap-2 text-[11px] sm:grid-cols-3">
          <Input label="Marca detectada" value={form.trigger_brand} onChange={(v) => setForm({ ...form, trigger_brand: v })} />
          <Input label="Categoría" value={form.trigger_category} onChange={(v) => setForm({ ...form, trigger_category: v })} />
          <Input label="País" value={form.country} onChange={(v) => setForm({ ...form, country: v })} />
          <Input label="Prioridad" type="number" value={String(form.priority)} onChange={(v) => setForm({ ...form, priority: Number(v) || 100 })} />
          <label className="col-span-2 sm:col-span-3">
            <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Campaña *</div>
            <select
              value={form.campaign_id}
              onChange={(e) => setForm({ ...form, campaign_id: e.target.value })}
              className="mt-1 w-full rounded-lg border border-border bg-background px-2 py-1.5 text-[11px]"
            >
              <option value="">Selecciona…</option>
              {campaigns.map((c) => (
                <option key={c.id} value={c.id}>{c.name} — {c.sponsor_brand}</option>
              ))}
            </select>
          </label>
        </div>
        <button
          disabled={saving || !form.campaign_id}
          onClick={async () => {
            setSaving(true);
            try {
              await onSave({
                data: {
                  trigger_brand: form.trigger_brand.trim() || undefined,
                  trigger_category: form.trigger_category.trim() || undefined,
                  country: form.country.trim() || undefined,
                  priority: form.priority,
                  campaign_id: form.campaign_id,
                  is_active: true,
                },
              });
              setForm({ trigger_brand: "", trigger_category: "", country: "", priority: 100, campaign_id: "" });
              await onRefresh();
            } finally { setSaving(false); }
          }}
          className="mt-2 rounded-lg bg-primary px-3 py-1.5 text-[11px] font-black text-primary-foreground disabled:opacity-50"
        >
          {saving ? "Guardando…" : "Crear regla"}
        </button>
      </div>
      <div className="overflow-x-auto rounded-xl border border-border bg-background/60 p-3">
        <h2 className="text-sm font-black">Reglas</h2>
        <table className="mt-2 w-full text-left text-[11px]">
          <thead className="text-muted-foreground">
            <tr><th className="px-2 py-1">Marca→</th><th className="px-2 py-1">Categoría</th><th className="px-2 py-1">País</th><th className="px-2 py-1">Prio</th><th className="px-2 py-1">Campaña</th><th className="px-2 py-1">Estado</th></tr>
          </thead>
          <tbody>
            {rules.map((r) => {
              const c = campaigns.find((x) => x.id === r.campaign_id);
              return (
                <tr key={r.id} className="border-t border-border/40">
                  <td className="px-2 py-1 font-black">{r.trigger_brand ?? "*"}</td>
                  <td className="px-2 py-1">{r.trigger_category ?? "*"}</td>
                  <td className="px-2 py-1">{r.country ?? "*"}</td>
                  <td className="px-2 py-1">{r.priority}</td>
                  <td className="px-2 py-1">{c ? `${c.name} — ${c.sponsor_brand}` : r.campaign_id}</td>
                  <td className="px-2 py-1">
                    <button
                      onClick={async () => { await onToggle({ data: { id: r.id, is_active: !r.is_active } }); await onRefresh(); }}
                      className={`rounded-md px-2 py-0.5 text-[10px] font-bold ${
                        r.is_active ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {r.is_active ? "Activa" : "Inactiva"}
                    </button>
                  </td>
                </tr>
              );
            })}
            {rules.length === 0 && (
              <tr><td colSpan={6} className="px-2 py-3 text-center text-muted-foreground">Sin reglas.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/* ============= Analytics ============= */
function AnalyticsPanel({ rows }: { rows: CommercialAnalyticsRow[] }) {
  return (
    <section className="overflow-x-auto rounded-xl border border-border bg-background/60 p-3">
      <h2 className="text-sm font-black">Analytics por campaña (30 días)</h2>
      <table className="mt-2 w-full text-left text-[11px]">
        <thead className="text-muted-foreground">
          <tr><th className="px-2 py-1">Campaña</th><th className="px-2 py-1">Marca</th><th className="px-2 py-1">Impr.</th><th className="px-2 py-1">Clics</th><th className="px-2 py-1">CTR</th><th className="px-2 py-1">Conv.</th><th className="px-2 py-1">Shares</th><th className="px-2 py-1">Únicos</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.campaign_id} className="border-t border-border/40">
              <td className="px-2 py-1 font-black">{r.campaign_name}</td>
              <td className="px-2 py-1">{r.sponsor_brand}</td>
              <td className="px-2 py-1">{r.impressions}</td>
              <td className="px-2 py-1">{r.clicks}</td>
              <td className="px-2 py-1">{r.ctr}%</td>
              <td className="px-2 py-1">{r.conversions}</td>
              <td className="px-2 py-1">{r.shares}</td>
              <td className="px-2 py-1">{r.unique_users}</td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={8} className="px-2 py-3 text-center text-muted-foreground">Aún no hay telemetría.</td></tr>
          )}
        </tbody>
      </table>
    </section>
  );
}

/* ============= Timeline ============= */
function TimelinePanel({ events }: { events: CommercialEventRow[] }) {
  return (
    <section className="rounded-xl border border-border bg-background/60 p-3">
      <h2 className="text-sm font-black">Timeline de eventos comerciales</h2>
      <ol className="mt-2 space-y-2">
        {events.map((e) => (
          <li key={e.id} className="flex items-start gap-3 rounded-lg border border-border/40 p-2 text-[11px]">
            <div className="w-24 shrink-0 text-muted-foreground">
              {new Date(e.detected_at).toLocaleString()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-black">{e.brand_name}{e.category ? ` · ${e.category}` : ""}</div>
              <div className="text-muted-foreground">
                {[e.channel, e.country, e.match_id, `${e.duration_seconds}s`].filter(Boolean).join(" · ")}
              </div>
            </div>
          </li>
        ))}
        {events.length === 0 && (
          <li className="px-2 py-3 text-center text-[11px] text-muted-foreground">Sin actividad.</li>
        )}
      </ol>
    </section>
  );
}

/* ============= Shared input ============= */
function Input({
  label, value, onChange, type = "text",
}: { label: string; value: string; onChange: (v: string) => void; type?: string }) {
  return (
    <label className="block">
      <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">{label}</div>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-lg border border-border bg-background px-2 py-1.5 text-[11px]"
      />
    </label>
  );
}